import { useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { adminApi } from '../api/admin';
import type { SurveyAppVersionCount, SurveyKey, SurveyResponse } from '../api/types';
import { fmtDate, fmtNum } from '../lib/format';

type KeyFilter = SurveyKey | 'all';
type SentimentFilter = 'all' | 'up' | 'down';

/** The 6 prompts the iOS client emits, plus human labels for the segmented filter. */
const SURVEY_KEYS: { key: KeyFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'purchase_reason', label: 'Purchase reason' },
  { key: 'paywall_dismiss', label: 'Paywall dismiss' },
  { key: 'welcome_back', label: 'Welcome back' },
  { key: 'high_value_scan', label: 'High-value scan' },
  { key: 'set_complete', label: 'Set complete' },
  { key: 'bulk_scan', label: 'Bulk scan' },
];

/**
 * Read-only viewer for in-app survey / micro-feedback (the 6-trigger system).
 * Backed by GET /admin/surveys. Free-text is shown prominently — it's the
 * qualitative gold. The top aggregate gives the PM per-option + 👍/👎
 * distributions at a glance for the selected survey.
 */
const ALL_VERSIONS = 'all';

export function SurveysScreen() {
  const [surveyKey, setSurveyKey] = useState<KeyFilter>('all');
  const [sentiment, setSentiment] = useState<SentimentFilter>('all');
  const [appVersion, setAppVersion] = useState<string>(ALL_VERSIONS);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'surveys', surveyKey, sentiment, appVersion],
    queryFn: () =>
      adminApi.listSurveyResponses({
        survey_key: surveyKey === 'all' ? undefined : surveyKey,
        sentiment: sentiment === 'all' ? undefined : sentiment,
        app_version: appVersion === ALL_VERSIONS ? undefined : appVersion,
        limit: 500,
      }),
  });

  const items = data?.items ?? [];
  const optionCounts = data?.aggregate.optionCounts ?? [];
  const sentimentCounts = data?.aggregate.sentimentCounts ?? [];
  const appVersions = data?.availableAppVersions ?? [];

  // Keep the dropdown honest: if the selected version disappears from the
  // scoped set (e.g. the survey/date filter changed and no rows for that
  // version remain), fall back to All so the query isn't stuck on a dead value.
  if (
    appVersion !== ALL_VERSIONS &&
    appVersions.length > 0 &&
    !appVersions.some((v) => v.version === appVersion)
  ) {
    setAppVersion(ALL_VERSIONS);
  }

  const up = sentimentCounts
    .filter((s) => s.sentiment === 'up')
    .reduce((n, s) => n + s.count, 0);
  const down = sentimentCounts
    .filter((s) => s.sentiment === 'down')
    .reduce((n, s) => n + s.count, 0);

  // Option distribution, largest first. Null option_key rows are folded into a
  // "(no option)" bucket so free-text-only submissions still show a count.
  const options = [...optionCounts].sort((a, b) => b.count - a.count);
  const optionTotal = options.reduce((n, o) => n + o.count, 0);

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1080 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>
          Surveys &amp; Feedback
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: 0, lineHeight: 1.6 }}>
          In-app micro-feedback from the 6 contextual prompts. Read-only. Free-text is the
          qualitative gold — skim it first.
        </p>
      </div>

      {/* Filters — each control is a labeled FilterGroup so adding date/rating/
          etc. later is a one-liner (drop in another FilterGroup + its state). */}
      <Card padding={0}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
          <FilterGroup label="Survey">
            <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-pill)', flexWrap: 'wrap' }}>
              {SURVEY_KEYS.map(({ key, label }) => (
                <SegButton key={key} active={surveyKey === key} onClick={() => setSurveyKey(key)}>
                  {label}
                </SegButton>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Sentiment">
            <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-pill)' }}>
              {(['all', 'up', 'down'] as const).map((s) => (
                <SegButton key={s} active={sentiment === s} onClick={() => setSentiment(s)}>
                  {s === 'all' ? 'Any' : s === 'up' ? '👍 Up' : '👎 Down'}
                </SegButton>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="App version">
            <VersionSelect
              value={appVersion}
              versions={appVersions}
              onChange={setAppVersion}
            />
          </FilterGroup>

          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
            {fmtNum(items.length)} response{items.length === 1 ? '' : 's'}
          </div>
        </div>
      </Card>

      {error ? (
        <Card style={{ color: 'var(--bad)', fontSize: 13 }}>
          Failed to load surveys: {(error as Error).message}
        </Card>
      ) : (
        <>
          {/* Aggregate summary */}
          <Card style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)' }}>
                Sentiment
              </span>
              <Pill tone="good" size="md">👍 {fmtNum(up)}</Pill>
              <Pill tone="bad" size="md">👎 {fmtNum(down)}</Pill>
              {up + down === 0 && (
                <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                  No sentiment recorded for this filter.
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)' }}>
                Option distribution
              </span>
              {options.length === 0 ? (
                <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>No option responses for this filter.</span>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {options.map((o, i) => {
                    const pct = optionTotal > 0 ? (o.count / optionTotal) * 100 : 0;
                    return (
                      <div key={`${o.surveyKey}-${o.optionKey ?? 'null'}-${i}`} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 56px', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 12.5, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {surveyKey === 'all' && <span style={{ color: 'var(--ink-4)' }}>{o.surveyKey}·</span>}
                          {o.optionKey ?? '(no option)'}
                        </span>
                        <div style={{ height: 8, borderRadius: 999, background: 'var(--bg-sunken)', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--brand)', borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: 12.5, color: 'var(--ink-2)', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {fmtNum(o.count)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Response list */}
          <Card padding={0}>
            {items.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                {isLoading ? 'Loading…' : 'No responses match this filter.'}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 1, background: 'var(--line)' }}>
                {items.map((r) => (
                  <ResponseRow key={r.id} r={r} showKey={surveyKey === 'all'} />
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * A labeled wrapper around one filter control. The uniform label + spacing is
 * what makes the filter row extensible — a new filter is just another
 * `<FilterGroup label="…">…</FilterGroup>` with its own state/control.
 */
function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)' }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * App-version filter: a native <select> ("All versions" + each distinct version
 * with its count). A dropdown (not segmented pills) because the version set is
 * unbounded and grows every release. Options come from the list response's
 * `availableAppVersions`, already newest-first.
 */
function VersionSelect({
  value,
  versions,
  onChange,
}: {
  value: string;
  versions: SurveyAppVersionCount[];
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '7px 10px',
        borderRadius: 'var(--r-pill)',
        fontSize: 12,
        fontWeight: 500,
        background: 'var(--bg-sunken)',
        color: 'var(--ink)',
        border: '1px solid var(--line)',
        cursor: 'pointer',
        minWidth: 120,
        fontFamily: 'var(--font-mono)',
      }}
    >
      <option value={ALL_VERSIONS}>All versions</option>
      {versions.map((v) => (
        <option key={v.version} value={v.version}>
          v{v.version} ({v.count})
        </option>
      ))}
    </select>
  );
}

function SegButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 'var(--r-pill)',
        fontSize: 12,
        fontWeight: 500,
        background: active ? 'var(--bg-elev)' : 'transparent',
        color: active ? 'var(--ink)' : 'var(--ink-3)',
        boxShadow: active ? 'var(--shadow-card)' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function ResponseRow({ r, showKey }: { r: SurveyResponse; showKey: boolean }) {
  return (
    <div style={{ background: 'var(--bg-elev)', padding: '14px 18px', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {showKey && <Pill tone="brand" size="sm">{r.surveyKey}</Pill>}
        {r.sentiment === 'up' && <Pill tone="good" size="sm">👍</Pill>}
        {r.sentiment === 'down' && <Pill tone="bad" size="sm">👎</Pill>}
        {r.optionKey && (
          <span style={{ fontSize: 12.5, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>{r.optionKey}</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--ink-3)' }}>{fmtDate(r.createdAt)}</span>
      </div>

      {r.freeText && (
        <div style={{ fontSize: 14, color: 'var(--ink)', whiteSpace: 'pre-wrap', lineHeight: 1.5, fontWeight: 500 }}>
          “{r.freeText}”
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
        <span>trigger: {r.trigger}</span>
        {r.platform && <span>· {r.platform}</span>}
        {r.appVersion && <span>· v{r.appVersion}</span>}
        {r.deviceId && (
          <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {r.deviceId}</span>
        )}
      </div>
    </div>
  );
}
